import {
  DataGridFilter,
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import { PickupRequestsListRequest } from "fe-shared-requests/src/types";

type RequestParams = Pick<
  PickupRequestsListRequest,
  "filteringParameters" | "sortingBy"
>;

export const DEFAULT_SORT_FIELD = "submittedDate";

// The hauler column carries no entry because it is not sortable at all: Request.GetPickupRequests
// has no order-by branch for TargetCompanyName, and an unmapped field would silently fall back to
// the procedure's SubmittedDate default.
const sortFieldMap: { [key: string]: string } = {
  submittedDate: "SubmittedDate",
  requestNumber: "RequestNumber",
  pickupDate: "PickupDateFrom",
  statusCode: "StatusCode",
  sourceCompanyName: "SourceCompanyName",
  municipalityName: "MunicipalityName",
  zoneName: "ZoneName",
};

// Request.GetPickupRequests has no company-name filter of either side - ParticipantName matches
// the source or the target company name.
const filterFieldMap: { [key: string]: string } = {
  targetCompanyName: "ParticipantName",
  sourceCompanyName: "ParticipantName",
  carrierFirstName: "CarrierName",
};

const getFilterValues = (filter: DataGridFilter) => {
  switch (filter.type) {
    case "dateRangeFilter": {
      const [startDate, endDate] = filter.value;

      if (startDate) {
        return [startDate, endDate || startDate]
          .map((date) => new Date(date).toJSON())
          .join(",");
      }
      return "";
    }
    case "selectFilter":
      if (filter.value.length) {
        return filter.value.join(",");
      }
      return "";
    default:
      return filter.value;
  }
};

const getRequestParams = (
  filters: DataGridFilters,
  options: DataGridOptions
): RequestParams => ({
  filteringParameters: Object.keys(filters).reduce(
    (
      acc: PickupRequestsListRequest["filteringParameters"],
      propertyName: string
    ) => {
      const filter = filters[propertyName];
      const values = getFilterValues(filter);

      if (values) {
        acc.push({
          propertyName: filterFieldMap[propertyName] || propertyName,
          // The grid groups the number with spaces for display, the server matches it unspaced.
          values:
            propertyName === "requestNumber"
              ? values.replace(/\s/g, "")
              : values,
        });
      }
      return acc;
    },
    []
  ),
  sortingBy: {
    propertyName:
      sortFieldMap[options.sortField] || sortFieldMap[DEFAULT_SORT_FIELD],
    isAscending: options.sortDirection === "asc",
  },
});

export default getRequestParams;
